

export const dynamic = "force-dynamic";

import { useState, useEffect, useMemo } from "react";
import { 
  Search, 
  CreditCard,
  Download,
  FileText,
  User,
  Calendar,
} from "lucide-react";
// @ts-ignore - react-csv doesn't have types
import { CSVLink } from "react-csv";

// INR Icon Component
const INRIcon = ({ className }: { className?: string }) => (
  <span className={className}>₹</span>
);
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import DateRangePicker from "../components/DateRangePicker";
import SearchableDropdown from "../components/SearchableDropdown";
import { clientsApi, clientPaymentsApi, eventsApi, mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatCurrencyFull, formatDate } from "@/lib/data";
import { byNewest } from "@/lib/utils";
import { ReportTable } from "../components/ReportTable";

// Types
interface ClientPayment {
  id: number;
  client_id: number;
  client?: { id: number; name: string; email?: string; phone?: string };
  amount: number;
  payment_date: string;
  payment_method: string;
  reference_number?: string;
  notes?: string;
  invoice_id?: number;
  invoice?: { id: number; invoice_number: string };
  event_id: number; // Made required
  event?: { id: number; name: string; start_date?: string; client_name?: string };
  created_at?: string;
}

interface Client {
  id: number;
  name: string;
  email?: string;
  balance: number;
}

interface Event {
  id: number;
  name: string;
  client_id?: number;
  start_date: string;
  financials?: {
    customer?: {
      balance: number;
      total: number;
      paid: number;
    };
  };
}

// Payment Detail Modal Component
function PaymentDetailModal({ 
  payment, 
  isOpen, 
  onClose 
}: { 
  payment: ClientPayment | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!payment) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Payment Details"
      size="lg"
    >
      <div className="space-y-6">
        {/* Payment Information */}
        <div className="bg-stone-50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-600" />
            Payment Information
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-stone-500">Amount:</span>
              <p className="font-semibold text-emerald-600 text-lg">{formatCurrencyFull(payment.amount)}</p>
            </div>
            <div>
              <span className="text-stone-500">Payment Date:</span>
              <p className="font-medium">{formatDate(payment.payment_date)}</p>
            </div>
            <div>
              <span className="text-stone-500">Payment Method:</span>
              <p className="font-medium capitalize">{payment.payment_method}</p>
            </div>
            <div>
              <span className="text-stone-500">Reference:</span>
              <p className="font-medium">{payment.reference_number || 'N/A'}</p>
            </div>
            {payment.notes && (
              <div className="col-span-2">
                <span className="text-stone-500">Notes:</span>
                <p className="font-medium mt-1">{payment.notes}</p>
              </div>
            )}
            {payment.invoice && (
              <div>
                <span className="text-stone-500">Invoice:</span>
                <p className="font-medium">{payment.invoice.invoice_number}</p>
              </div>
            )}
          </div>
        </div>

        {/* Client Information */}
        <div className="bg-stone-50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
            <User className="w-4 h-4 text-amber-600" />
            Client Information
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-stone-500">Client Name:</span>
              <p className="font-medium">{payment.client?.name || 'Unknown'}</p>
            </div>
            {payment.client?.email && (
              <div>
                <span className="text-stone-500">Email:</span>
                <p className="font-medium">{payment.client.email}</p>
              </div>
            )}
            {payment.client?.phone && (
              <div>
                <span className="text-stone-500">Phone:</span>
                <p className="font-medium">{payment.client.phone}</p>
              </div>
            )}
          </div>
        </div>

        {/* Event Information */}
        <div className="bg-stone-50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-amber-600" />
            Event Information
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-stone-500">Event Name:</span>
              <p className="font-medium">{payment.event?.name || 'N/A'}</p>
            </div>
            <div>
              <span className="text-stone-500">Event Date:</span>
              <p className="font-medium">{payment.event?.start_date ? formatDate(payment.event.start_date) : 'N/A'}</p>
            </div>
            {payment.event?.client_name && (
              <div className="col-span-2">
                <span className="text-stone-500">Customer:</span>
                <p className="font-medium">{payment.event.client_name}</p>
              </div>
            )}
          </div>
        </div>

        {/* Close Button */}
        <div className="flex justify-end pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function ClientPaymentsPage() {
  const { showToast } = useToast();
  
  const [payments, setPayments] = useState<ClientPayment[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  
  // Date filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  const [viewingPayment, setViewingPayment] = useState<ClientPayment | null>(null);

  useEffect(() => {
    loadData();
    loadFilters();
  }, []);

  const loadFilters = async () => {
    try {
      // Load payment methods
      const pmRes = await mastersApi.paymentMethods.list({ status: 'active' });
      const pmData = pmRes.data?.data || pmRes.data || [];
      if (Array.isArray(pmData)) {
        setPaymentMethods(pmData.map((m: any) => m.name));
      }
    } catch (error) {
      console.error("Failed to load filters:", error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [paymentsRes, clientsRes, eventsRes] = await Promise.all([
        clientPaymentsApi.list(),
        clientsApi.list(),
        eventsApi.list()
      ]);
      
      // Handle different response structures
      const paymentsData = paymentsRes.data?.data || paymentsRes.data || [];
      // If it's a paginator (has data property), extract the array
      const paymentsArray = Array.isArray(paymentsData) ? paymentsData : (paymentsData.data || []);
      
      setPayments(paymentsArray);
      setClients(clientsRes.data?.data || clientsRes.data || []);
      setEvents(eventsRes.data?.data || eventsRes.data || []);
    } catch (error) {
      showToast("Failed to load payments", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredPayments = useMemo(() => {
    let filtered = Array.isArray(payments) ? payments : [];
    
    // Date range filter: either end can be left open
    if (fromDate || toDate) {
      filtered = filtered.filter(p => {
        const day = String(p.payment_date || "").slice(0, 10);
        return (!fromDate || day >= fromDate) && (!toDate || day <= toDate);
      });
    }
    
    // Search query filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        (p.client?.name || '').toLowerCase().includes(q) ||
        (p.reference_number || '').toLowerCase().includes(q) ||
        (p.notes || '').toLowerCase().includes(q) ||
        (p.event?.name || '').toLowerCase().includes(q)
      );
    }
    
    // Client filter
    if (clientFilter !== 'all') {
      filtered = filtered.filter(p => p.client_id === parseInt(clientFilter));
    }
    
    // Event filter
    if (eventFilter !== 'all') {
      filtered = filtered.filter(p => p.event_id === parseInt(eventFilter));
    }
    
    // Payment method filter
    if (paymentMethodFilter !== 'all') {
      const plain = (m?: string) => (m || "").replace(/_/g, " ").toLowerCase();
      filtered = filtered.filter(p => plain(p.payment_method) === plain(paymentMethodFilter));
    }
    
    // Newest-created payments first (copy: `filtered` may still be the state array).
    return [...filtered].sort(byNewest);
  }, [payments, searchQuery, clientFilter, eventFilter, paymentMethodFilter, fromDate, toDate]);

  const csvData = useMemo(() => {
    return filteredPayments.map(payment => ({
      'Payment ID': payment.id,
      'Client Name': payment.client?.name || 'Unknown',
      'Client Email': payment.client?.email || '',
      'Event Name': payment.event?.name || 'N/A',
      'Event Date': payment.event?.start_date || '',
      'Amount': payment.amount,
      'Payment Date': formatDate(payment.payment_date),
      'Payment Method': payment.payment_method,
      'Reference': payment.reference_number || '',
      'Invoice': payment.invoice?.invoice_number || '',
      'Notes': payment.notes || '',
    }));
  }, [filteredPayments]);

  const csvHeaders = [
    { label: 'Payment ID', key: 'Payment ID' },
    { label: 'Client Name', key: 'Client Name' },
    { label: 'Client Email', key: 'Client Email' },
    { label: 'Event Name', key: 'Event Name' },
    { label: 'Event Date', key: 'Event Date' },
    { label: 'Amount', key: 'Amount' },
    { label: 'Payment Date', key: 'Payment Date' },
    { label: 'Payment Method', key: 'Payment Method' },
    { label: 'Reference', key: 'Reference' },
    { label: 'Invoice', key: 'Invoice' },
    { label: 'Notes', key: 'Notes' },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 mb-1">Client Payments Report</h1>
        <p className="text-sm text-stone-500">Track and analyze client payment records</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row gap-3 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search payments..."
              className="w-full pl-9 pr-4 py-2.5 lg:py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-center gap-2 lg:flex-wrap">
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onFromChange={setFromDate}
            onToChange={setToDate}
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Events" },
              ...events.map((event) => ({
                value: String(event.id),
                label: event.name,
              })),
            ]}
            value={eventFilter}
            onChange={setEventFilter}
            placeholder="Filter by event"
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Clients" },
              ...clients.map((client) => ({
                value: String(client.id),
                label: client.name,
              })),
            ]}
            value={clientFilter}
            onChange={setClientFilter}
            placeholder="Filter by client"
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Methods" },
              ...paymentMethods.map((pm) => ({
                value: pm,
                label: pm,
              })),
            ]}
            value={paymentMethodFilter}
            onChange={setPaymentMethodFilter}
            placeholder="Payment method"
          />
          <CSVLink
            data={csvData}
            headers={csvHeaders}
            filename={`client-payments-${new Date().toISOString().split('T')[0]}.csv`}
            className="px-4 py-2.5 lg:py-2 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </CSVLink>
        </div>
      </div>

      {/* Content */}
      {filteredPayments.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <CreditCard className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No payments found</p>
        </div>
      ) : (
        <>
        <div className="hidden md:block">
        <ReportTable
          columns={[
            { key: 'client', label: 'Client', width: '25%' },
            { key: 'event', label: 'Event', width: '20%' },
            { key: 'date', label: 'Date', width: '15%' },
            { key: 'method', label: 'Method', width: '15%' },
            { key: 'reference', label: 'Reference', width: '15%' },
            { key: 'amount', label: 'Amount', align: 'right', width: '10%' },
          ]}
          data={filteredPayments.map(payment => ({
            id: payment.id,
            client: (
              <div>
                <span className="font-medium text-stone-800">{payment.client?.name || 'Unknown Client'}</span>
                {payment.reference_number && (
                  <p className="text-xs text-stone-400">Ref: {payment.reference_number}</p>
                )}
              </div>
            ),
            event: payment.event?.name || '-',
            date: formatDate(payment.payment_date),
            method: <span className="capitalize">{(payment.payment_method || "").replace(/_/g, " ")}</span>,
            reference: payment.reference_number || '-',
            amount: payment.amount,
          }))}
          totalField="amount"
          onRowClick={(payment) => setViewingPayment(filteredPayments.find(p => p.id === payment.id) || null)}
          formatValue={(value, key) => {
            if (key === 'amount') {
              return <span className="font-bold text-emerald-600">{formatCurrencyFull(value)}</span>;
            }
            return value;
          }}
        />
        </div>

        {/* Mobile cards (< md): tap a card to open the same detail modal as a table row */}
        <div className="md:hidden bg-white rounded-xl border border-stone-200 overflow-hidden divide-y divide-stone-100">
          {filteredPayments.map(payment => (
            <div
              key={payment.id}
              role="button"
              tabIndex={0}
              onClick={() => setViewingPayment(payment)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setViewingPayment(payment); } }}
              className="p-4 cursor-pointer hover:bg-stone-50 active:bg-stone-50 transition-colors outline-none focus-visible:bg-amber-50/50"
              aria-label={`View payment of ${formatCurrencyFull(payment.amount)} from ${payment.client?.name || 'Unknown Client'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-stone-800 truncate">{payment.client?.name || 'Unknown Client'}</p>
                  <p className="text-xs text-stone-500 mt-0.5 truncate">
                    {payment.event?.name || '-'} · {formatDate(payment.payment_date)}
                  </p>
                </div>
                <span className="font-bold text-emerald-600 whitespace-nowrap flex-shrink-0">{formatCurrencyFull(payment.amount)}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div className="min-w-0">
                  <p className="text-xs text-stone-400">Method</p>
                  <p className="text-stone-700 capitalize truncate">{payment.payment_method || '-'}</p>
                </div>
                <div className="min-w-0 text-right">
                  <p className="text-xs text-stone-400">Method</p>
                  <p className="text-stone-700 truncate capitalize">{(payment.payment_method || "-").replace(/_/g, " ")}</p>
                </div>
                {payment.reference_number && (
                  <div className="min-w-0 col-span-2">
                    <p className="text-xs text-stone-400">Ref</p>
                    <p className="text-stone-700 truncate">{payment.reference_number}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="px-4 py-3 bg-stone-50 flex items-center justify-between gap-3 text-sm font-bold">
            <span className="text-stone-800">Total</span>
            <span className="text-emerald-600">
              {formatCurrencyFull(filteredPayments.reduce((sum, p) => sum + (parseFloat(String(p.amount)) || 0), 0))}
            </span>
          </div>
        </div>
        </>
      )}

      {/* Payment Detail Modal */}
      <PaymentDetailModal
        payment={viewingPayment}
        isOpen={!!viewingPayment}
        onClose={() => setViewingPayment(null)}
      />
    </DashboardLayout>
  );
}
